import classNames from 'classnames'
import { FC, memo, useCallback, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { EntityType, UnreadInfoTarget } from '@crew/enums/domain'
import { getDefaultTabValue } from '@crew/utils/enum'
import { ActiveChatRoomInfo } from '@crew/states'
import { generateImageAvatarUrl } from '@crew/utils/avatar'

import {
  CrewAvatar,
  CrewAvatarShapeType,
  CrewAvatarSize,
} from 'components/elements/crewAvatar/crewAvatar'
import { CrewChatRoomUnreadBadge } from 'components/elements/crewChatRoomUnreadBadge/crewChatRoomUnreadBadge'

import { LeftSideBarContextMenu } from '../../../../../leftSideBarContextMenu/leftSideBarContextMenu'
import { GroupChatTabs } from 'enums/app'

export type FavoriteChatListGroupChatItemProps = {
  chatRoom: ActiveChatRoomInfo
}

// メニューの基本スタイル
const baseClassNames =
  'flex flex-row h-full w-full items-center px-2 py-1.5 gap-2 hover:font-bold [&:has(.has-unread)]:font-bold group-hover:font-bold'

// 選択メニューのアクティブ状態スタイル（ハイライト）
const activeClassNames = 'font-bold crew-text-gray-1 crew-bg-gray-2'

// お気に入りチャットリスト内のグループチャットのリストアイテム
export const FavoriteChatListGroupChatItem: FC<FavoriteChatListGroupChatItemProps> =
  memo((props) => {
    const [hover, setHover] = useState(false)
    const location = useLocation()
    // ホバーメニューを表示
    const handleMouseEnter = useCallback(() => {
      setHover(true)
    }, [])

    // ホバーメニューを非表示
    const handleMouseLeave = useCallback(() => {
      setHover(false)
    }, [])

    // URLの変更に応じてハイライトさせるかどうかをハンドリングする
    const isActive = useMemo(
      () =>
        // eg: location.pathname = /group-chats/{projectId}/xxx
        // projectIdのみで判定する。projectIdで一意に識別出来るので、他の条件は不要
        location.pathname.split('/')[2] === props.chatRoom.projectId,
      [location, props.chatRoom.projectId]
    )

    return (
      <li
        // Warning表示回避のkey指定
        key={props.chatRoom.projectId}
        className="flex flex-row group relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <NavLink
          // グループチャット詳細画面に遷移する
          to={`group-chats/${props.chatRoom.projectId}/${getDefaultTabValue(
            GroupChatTabs
          )}`}
          className={classNames(baseClassNames, isActive && activeClassNames)}
        >
          {/* アバター */}
          {/* CREW-21003で正式に実装するため、現状は暫定実装
              https://break-tmc.atlassian.net/browse/CREW-21003 */}
          <CrewAvatar
            cacheValue={
              props.chatRoom.projectId + props.chatRoom.projectVersion
            }
            displayName={props.chatRoom.name}
            shape={CrewAvatarShapeType.Circle}
            imageURL={generateImageAvatarUrl(
              EntityType.Project,
              props.chatRoom.projectId
            )}
            size={CrewAvatarSize.xs}
          />

          {/* グループチャット名 */}
          <span className="line-clamp-2">
            {props.chatRoom.projectMemberCount
              ? `${props.chatRoom.name} (${props.chatRoom.projectMemberCount})`
              : props.chatRoom.name}
          </span>
          <CrewChatRoomUnreadBadge chatRoomId={props.chatRoom.chatRoomId} />
        </NavLink>

        <LeftSideBarContextMenu
          target={UnreadInfoTarget.ChatRoom}
          chatRoomId={props.chatRoom.chatRoomId}
          projectId={props.chatRoom.projectId}
          hover={hover}
          isActive={isActive}
          isFavorite={props.chatRoom.isFavorite}
          enableMarkAllRead={false} // 「チャット」欄では「すべて既読にする」は非表示（すべて既読済みのため）
        />
      </li>
    )
  })
